import React, { useEffect, useState, memo } from "react";
import {
  TextField, Button, Grid, Box, Switch, FormControlLabel, FormControl, Dialog, DialogActions, DialogContent, DialogTitle
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import { PostingSetupViewModel } from "../../../Types/ExportTypes";
import DefaultActions from "../../../components/Card/DefaultActions";
import LocalizedNumberInputs from "../../../components/Input/LocalizedNumberInputs"
import { useTranslation } from "react-i18next";
import { ProductPostingGroupAutoComplete, VatbusPostingGroupAutoComplete, VatTypeAutoComplete, AccountAutoComplete } from "../../../components/AutoComplete/AutoCompletes";
const PostingSetupEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { id = null } = params || {};
  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { goBack, navigate } = useNavigation();
  const [viewModel, setViewModel] = useState<PostingSetupViewModel | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {
    if (isLoading) showLoading();
    else hideLoading();
  }, [isLoading, showLoading, hideLoading]);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      authService
        .get<PostingSetupViewModel>(`/settingsApi/GetPostingSetup?id=${id}`)
        .then(setViewModel)
        .catch((error: any) => {
          showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
        })
        .finally(() => setIsLoading(false));
    } else {
      authService
        .get<PostingSetupViewModel>(`/settingsApi/SuggestPostingSetupAccounts`)
        .then((suggestedAccounts) => {
          setViewModel({
            createNew: true,
            salesAccount: suggestedAccounts.salesAccount,
            salesCreditAccount: suggestedAccounts.salesCreditAccount,
            salesDiscountAccount: suggestedAccounts.salesDiscountAccount,
            purchaseAccount: suggestedAccounts.purchaseAccount,
            purchaseCreditAccount: suggestedAccounts.purchaseCreditAccount,
            purchaseDiscountAccount: suggestedAccounts.purchaseDiscountAccount,
            salesVATAccount: suggestedAccounts.salesVATAccount,
            purchaseVATAccount: suggestedAccounts.purchaseVATAccount,
            purchaseReverseVATAccount: suggestedAccounts.purchaseReverseVATAccount,
            inventoryAccount: suggestedAccounts.inventoryAccount,
            inventoryAdjustment: suggestedAccounts.inventoryAdjustment,
            cogsAccount: suggestedAccounts.cogsAccount,
            purchaseRecievalAccount: suggestedAccounts.purchaseRecievalAccount,
            purchaseOverheadAccount: suggestedAccounts.purchaseOverheadAccount,
            transferBalanceAccount: suggestedAccounts.transferBalanceAccount,
            externalReference: suggestedAccounts.externalReference,
            inwardGoodsAccount: suggestedAccounts.inwardGoodsAccount,
            inwardServiceAccount: suggestedAccounts.inwardServiceAccount,
            inwardGoodsVATAccount: suggestedAccounts.inwardGoodsVATAccount,
            inwardServiceVATAccount: suggestedAccounts.inwardServiceVATAccount,
            inventoryValueAdjustmentAccountNo: suggestedAccounts.inventoryValueAdjustmentAccountNo,
            purchaseOverheadCustomsAccount: suggestedAccounts.purchaseOverheadCustomsAccount,
            purchaseOverheadOtherAccount: suggestedAccounts.purchaseOverheadOtherAccount,
            purchaseOverheadFreightAccount: suggestedAccounts.purchaseOverheadFreightAccount,
            inventoryReevaluationAccountNo: suggestedAccounts.inventoryReevaluationAccountNo,
            inventoryAccountNoDesc: suggestedAccounts.inventoryAccountNoDesc,
          });
        })
        .catch((error: any) => {
          showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
          setViewModel({ createNew: true }); // Fallback in case of failure
        })
        .finally(() => setIsLoading(false));
    }
  }, [id, authService]);

  const handleSave = async () => {
    if (!viewModel) return;
    if (!validateViewModel()) return; // Prevent save if validation fails
    var newItem = viewModel.createNew ?? false;

    setIsLoading(true);
    try {
      const response = await authService.post<PostingSetupViewModel>("/settingsApi/CreateOrUpdatePostingSetup", viewModel);
      setViewModel(response);
      showSnackbar(t("dialog.savedSuccessfully"), "success");

      if (newItem) {
        navigate({ path: "/settings/postingsetup/edit", params: { id: response.id } });
      }
    } catch (error: any) {
      showSnackbar(t("dialog.saveErrorGeneric"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof PostingSetupViewModel, value: any) => {
    setViewModel((prev) => (prev ? { ...prev, [field]: value } : null));
  };


  const handleDelete = async () => {

    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;
    var newItem = viewModel?.createNew ?? false;
    if (newItem) {
      navigate({ path: "/settings/postingsetup/list" });
      return;
    }

    setIsLoading(true);
    try {
      await authService.post(`/settingsApi/DeletePostingSetup`, viewModel);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");;


      navigate({ path: "/settings/postingsetup/list" });
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
    }
  };





  const validateViewModel = () => {
    if (!viewModel) {
      showSnackbar(t("dialog.dataMissing"), "error");
      return false;
    }

    if (
      !viewModel.businessPostingGroupKey?.trim() &&
      !viewModel.productPostingGroupKey?.trim()
    ) {
      showSnackbar(t("dialog.postingGroupWhatOrWhoRequired"), "warning");
      return false;
    }

    if (!viewModel.salesAccount?.trim()) {
      showSnackbar(t("dialog.salesAccountMissing"), "warning");
      return false;
    }

    if (!viewModel.salesVATAccount?.trim()) {
      showSnackbar(t("dialog.salesVatAccountMissing"), "warning");
      return false;
    }


    return true;
  };
  return (
    <>

      <CenteredCard title={t("common.edit") + " " + t("common.postingSetup")} isLoading={isLoading} handleSave={handleSave}>
        <Box sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          maxWidth: "fit-content",
          mb: 2
        }}>
          <DefaultActions
            onSave={handleSave}
            onDelete={handleDelete}
          />
        </Box>
        {viewModel && (
          <>
            <Grid container spacing={2}>


              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <VatbusPostingGroupAutoComplete
                    key={viewModel.businessPostingGroupKey || null}
                    valueId={viewModel.businessPostingGroupKey || null}
                    onChange={(selected) => handleChange("businessPostingGroupKey", selected?.value || "")}
                    infoText={t("common.postingSetupWho")}
                    helperText={t("help.postingSetupWho")}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <ProductPostingGroupAutoComplete
                    key={viewModel.productPostingGroupKey || null}
                    valueId={viewModel.productPostingGroupKey || null}
                    onChange={(selected) => handleChange("productPostingGroupKey", selected?.value || "")}
                    infoText={t("common.postingSetupWhat")}
                    helperText={t("help.postingSetupWhat")}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>


                <LocalizedNumberInputs
                  label={t("common.vatPercentage")}
                  value={viewModel?.vat ?? 0}
                  onValueChange={(value) => {
                    handleChange("vat", value ?? null);
                  }}
                  required
                  allowNegative={false} // Prevents negative numbers
                  decimalScale={2} // Ensures max 2 decimals
                  thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                  decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                  helperText={t("help.vatPercentage")}
                />


              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("common.startDate")}
                  type="date"
                  value={viewModel?.startDate ? viewModel.startDate.split("T")[0] : null}
                  onChange={(e) => handleChange("startDate", e.target.value)}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <VatTypeAutoComplete
                    key={viewModel.vatType || null}
                    valueId={viewModel.vatType || null}
                    onChange={(selected) => handleChange("vatType", selected?.value || "")}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <AccountAutoComplete
                    key={viewModel.salesAccount || null}
                    valueId={viewModel.salesAccount || null}
                    onChange={(selected) => handleChange("salesAccount", selected?.value || "")}
                    infoText={t("help.salesAccount")}
                    label={t("common.salesAccount")}
                    error={!viewModel.salesAccount || viewModel.salesAccount.trim() === ""}
                    helperText={
                      !viewModel.salesAccount?.trim()
                        ? t("help.salesAccountRequired")
                        : ""
                    }
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <AccountAutoComplete
                    key={viewModel.salesCreditAccount || null}
                    valueId={viewModel.salesCreditAccount || null}
                    onChange={(selected) => handleChange("salesCreditAccount", selected?.value || "")}
                    infoText={t("help.salesCreditAccount")}
                    label={t("common.salesCreditAccount")}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <AccountAutoComplete
                    key={viewModel.salesDiscountAccount || null}
                    valueId={viewModel.salesDiscountAccount || null}
                    onChange={(selected) => handleChange("salesDiscountAccount", selected?.value || "")}
                    infoText={t("help.salesDiscountAccount")}
                    label={t("common.salesDiscountAccount")}
                  />
                </FormControl>
              </Grid>



              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <AccountAutoComplete
                    key={viewModel.salesVATAccount || null}
                    valueId={viewModel.salesVATAccount || null}
                    onChange={(selected) => handleChange("salesVATAccount", selected?.value || "")}
                    infoText={t("help.salesVatAccount")}
                    label={t("common.salesVatAccount")}
                    error={!viewModel.salesVATAccount || viewModel.salesVATAccount.trim() === ""}
                    helperText={
                      !viewModel.salesVATAccount?.trim()
                        ? t("help.salesVatAccountRequired")
                        : ""
                    }
                  />
                </FormControl>
              </Grid>








              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={showSettings} onChange={(e) => setShowSettings(e.target.checked)} />}
                  label={t("common.showMoreSettings")}
                />
              </Grid>
              {(showSettings) && (
                <>
                  <Box
                    sx={{
                      backgroundColor: viewModel.createNew ? "transparent" : "secondary.main",
                      padding: 2,
                      borderRadius: 2,
                      width: "100%"
                    }}
                  >

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <AccountAutoComplete
                          key={viewModel.purchaseAccount || null}
                          valueId={viewModel.purchaseAccount || null}
                          onChange={(selected) => handleChange("purchaseAccount", selected?.value || "")}
                          label={t("common.purchaseAccount")}
                          infoText={t("help.purchaseAccount")}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <AccountAutoComplete
                          key={viewModel.purchaseCreditAccount || null}
                          valueId={viewModel.purchaseCreditAccount || null}
                          onChange={(selected) => handleChange("purchaseCreditAccount", selected?.value || "")}
                          label={t("common.purchaseCreditAccount")}
                          infoText={t("help.purchaseCreditAccount")}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <AccountAutoComplete
                          key={viewModel.purchaseDiscountAccount || null}
                          valueId={viewModel.purchaseDiscountAccount || null}
                          onChange={(selected) => handleChange("purchaseDiscountAccount", selected?.value || "")}
                          label={t("common.purchaseDiscountAccount")}
                          infoText={t("help.purchaseDiscountAccount")}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <AccountAutoComplete
                          key={viewModel.purchaseRecievalAccount || null}
                          valueId={viewModel.purchaseRecievalAccount || null}
                          onChange={(selected) => handleChange("purchaseRecievalAccount", selected?.value || "")}
                          label={t("common.purchaseReceivalAccount")}
                          infoText={t("help.purchaseReceivalAccount")}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <AccountAutoComplete
                          key={viewModel.purchaseOverheadAccount || null}
                          valueId={viewModel.purchaseOverheadAccount || null}
                          onChange={(selected) => handleChange("purchaseOverheadAccount", selected?.value || "")}
                          label={t("common.purchaseOverheadAccount")}
                          infoText={t("help.purchaseOverheadAccount")}
                        />
                      </FormControl>
                    </Grid>


                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <AccountAutoComplete
                          key={viewModel.purchaseVATAccount || null}
                          valueId={viewModel.purchaseVATAccount || null}
                          onChange={(selected) => handleChange("purchaseVATAccount", selected?.value || "")}
                          label={t("common.purchaseVatAccount")}
                          infoText={t("help.purchaseVatAccount")}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <AccountAutoComplete
                          key={viewModel.purchaseReverseVATAccount || null}
                          valueId={viewModel.purchaseReverseVATAccount || null}
                          onChange={(selected) => handleChange("purchaseReverseVATAccount", selected?.value || "")}
                          label={t("common.purchaseReverseVatAccount")}
                          infoText={t("help.purchaseReverseVatAccount")}
                        />
                      </FormControl>
                    </Grid>


                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <AccountAutoComplete
                          key={viewModel.cogsAccount || null}
                          valueId={viewModel.cogsAccount || null}
                          onChange={(selected) => handleChange("cogsAccount", selected?.value || "")}
                          label={t("common.cogsAccount")}
                          infoText={t("help.cogsAccount")}
                        />
                      </FormControl>
                    </Grid>


                    {/*<Grid item xs={12}>*/}
                    {/*    <FormControl fullWidth margin="normal">*/}
                    {/*        <AccountAutoComplete*/}
                    {/*            key={viewModel.transferBalanceAccount || null}*/}
                    {/*            valueId={viewModel.transferBalanceAccount || null}*/}
                    {/*            onChange={(selected) => handleChange("transferBalanceAccount", selected?.value || "")}*/}
                    {/*            label={t("common.transferBalanceAccount")}*/}
                    {/*            infoText={t("help.transferBalanceAccount")}*/}
                    {/*        />*/}
                    {/*    </FormControl>*/}
                    {/*</Grid>*/}

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <AccountAutoComplete
                          key={viewModel.inwardGoodsAccount || null}
                          valueId={viewModel.inwardGoodsAccount || null}
                          onChange={(selected) => handleChange("inwardGoodsAccount", selected?.value || "")}
                          label={t("common.inwardGoodsAccount")}
                          infoText={t("help.inwardGoodsAccount")}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <AccountAutoComplete
                          key={viewModel.inwardServiceAccount || null}
                          valueId={viewModel.inwardServiceAccount || null}
                          onChange={(selected) => handleChange("inwardServiceAccount", selected?.value || "")}
                          label={t("common.inwardServiceAccount")}
                          infoText={t("help.inwardServiceAccount")}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <AccountAutoComplete
                          key={viewModel.inwardGoodsVATAccount || null}
                          valueId={viewModel.inwardGoodsVATAccount || null}
                          onChange={(selected) => handleChange("inwardGoodsVATAccount", selected?.value || "")}
                          label={t("common.inwardGoodsVatAccount")}
                          infoText={t("help.inwardGoodsVatAccount")}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <AccountAutoComplete
                          key={viewModel.inwardServiceVATAccount || null}
                          valueId={viewModel.inwardServiceVATAccount || null}
                          onChange={(selected) => handleChange("inwardServiceVATAccount", selected?.value || "")}
                          label={t("common.inwardServiceVatAccount")}
                          infoText={t("help.inwardServiceVatAccount")}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <AccountAutoComplete
                          key={viewModel.inventoryValueAdjustmentAccountNo || null}
                          valueId={viewModel.inventoryValueAdjustmentAccountNo || null}
                          onChange={(selected) => handleChange("inventoryValueAdjustmentAccountNo", selected?.value || "")}
                          label={t("common.inventoryValueAdjustmentAccount")}
                          infoText={t("help.inventoryValueAdjustmentAccount")}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <AccountAutoComplete
                          key={viewModel.purchaseOverheadCustomsAccount || null}
                          valueId={viewModel.purchaseOverheadCustomsAccount || null}
                          onChange={(selected) => handleChange("purchaseOverheadCustomsAccount", selected?.value || "")}
                          label={t("common.purchaseOverheadCustomsAccount")}
                          infoText={t("help.purchaseOverheadCustomsAccount")}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <AccountAutoComplete
                          key={viewModel.purchaseOverheadOtherAccount || null}
                          valueId={viewModel.purchaseOverheadOtherAccount || null}
                          onChange={(selected) => handleChange("purchaseOverheadOtherAccount", selected?.value || "")}
                          label={t("common.purchaseOverheadOtherAccount")}
                          infoText={t("help.purchaseOverheadOtherAccount")}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <AccountAutoComplete
                          key={viewModel.purchaseOverheadFreightAccount || null}
                          valueId={viewModel.purchaseOverheadFreightAccount || null}
                          onChange={(selected) => handleChange("purchaseOverheadFreightAccount", selected?.value || "")}
                          label={t("common.purchaseOverheadFreightAccount")}
                          infoText={t("help.purchaseOverheadFreightAccount")}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        value={viewModel.externalReference || ""}
                        onChange={(e) => handleChange("externalReference", e.target.value)}
                        fullWidth
                        margin="normal"
                        label={t("common.externalReference")}
                        helperText={t("help.externalReference")}

                      />
                    </Grid>
                  </Box>
                </>
              )}


            </Grid>
          </>
        )}
        <Box sx={{ marginTop: 2, textAlign: "center" }}>
          <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ marginRight: 2 }}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={goBack}>
            {t("common.back")}
          </Button>
        </Box>
      </CenteredCard>


    </>
  );
};

export default PostingSetupEditPage;
